import { Component, OnInit, AfterViewInit, inject, Renderer2, OnDestroy, signal, viewChild, ElementRef } from '@angular/core';
import { Ticket } from '@app/dtos/ticket';
import { User } from '../../components/user/user';
import { ParametersService } from '../../components/parameters/parameters.service';
import { ContactService } from '@app/services/contact.service';
import { NotificationService, PreviousRouteService, TranslationService } from '@app/services';
import { Machine } from '@app/dtos';
import { CookieService } from 'ngx-cookie-service';
import { first } from 'rxjs/operators';
import { UserManagerService } from '@app/managers';

declare var turnstile: any;

const TURNSTILE_SITE_KEY = '0x4AAAAAAFpNEVPpI2-UtNBr';
const TURNSTILE_ONLOAD_CALLBACK = 'onTurnstileLoad';

@Component({
    selector: 'app-support',
    templateUrl: './support.component.html',
    styleUrls: ['./support.component.scss'],
    standalone: false
})
export class SupportComponent implements OnInit, OnDestroy, AfterViewInit {
    ticket: Ticket = new Ticket();
    user: User;
    public topics: string[] = ['PP-Fitments', 'PP-Operationmanual', 'PP-Images', 'PP-Highlights', 'PP-Prospects', 'PP-Videos', 'PP-TechData', 'MP-Fitments',
        'MP-Operationmanual', 'MP-Images', 'MP-SparePartsBook', 'MP-Highlights', 'MP-ProductDigitization', 'MP-Prospects', 'MP-TechnicalSpecifications',
        'MP-Videos', 'H-Poetsem', 'H-HaytoolAssist', 'H-TramlineAssist', 'U-Login', 'CC-Configurator'];
    germanTranslations: any;
    private readonly renderer = inject(Renderer2);
    readonly captchaToken = signal<string | null>(null);
    readonly captchaInteractive = signal(false);
    private readonly turnstileContainer = viewChild<ElementRef<HTMLElement>>('turnstileContainer');
    private turnstileWidgetId: string | null = null;
    private destroyed = false;
    private script: HTMLScriptElement = this.renderer.createElement('script');

    constructor(
        private readonly parametersService: ParametersService,
        public readonly contactService: ContactService,
        public readonly notificationService: NotificationService,
        public readonly translationService: TranslationService,
        private readonly previousRouteService: PreviousRouteService,
        private readonly userManager: UserManagerService,
        private readonly cookieService: CookieService
    ) {
    }

    ngOnInit(): void {
        this.parametersService.user$.subscribe(x => {
            if (x) {
                this.ticket.lastName = x.lastName;
                this.ticket.firstName = x.firstName;
                this.ticket.email = x.email;
                this.user = x;
            } else if ((!this.previousRouteService.getPreviousUrl() || (this.previousRouteService.getPreviousUrl() === '/home'))
                && this.previousRouteService.getCurrentUrl() === '/contact') {
                this.topics = ['H-Poetsem', 'H-HaytoolAssist', 'H-TramlineAssist', 'U-Login', 'CC-Configurator'];
            }
        });
        const machineNrCache = sessionStorage['machineNrCache'];
        if (machineNrCache) {
            if ((!this.previousRouteService.getPreviousUrl() || (this.previousRouteService.getPreviousUrl().includes('/machine/')))
                && this.previousRouteService.getCurrentUrl() === '/contact') {
                this.ticket.topic = 'PP-Fitments';
            } else {
                this.ticket.topic = 'MP-Fitments';
            }

            this.ticket.machineNumber = machineNrCache;
            sessionStorage.removeItem('machineNrCache');
        }

        const serNrCache = sessionStorage['serNrCache'];
        if (serNrCache) {
            this.ticket.machinSernr = serNrCache;
            sessionStorage.removeItem('serNrCache');
        }

        const equNrCache = sessionStorage['equNrCache'];
        if (equNrCache) {
            this.ticket.machinEqunr = equNrCache;
            sessionStorage.removeItem('equNrCache');
        }

        const maktxCache = sessionStorage['maktxCache'];
        if (maktxCache) {
            this.ticket.machinText = maktxCache;
            sessionStorage.removeItem('maktxCache');
        }


        this.resetValidation();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        if (this.turnstileWidgetId !== null && this.isTurnstileAvailable()) {
            turnstile.remove(this.turnstileWidgetId);
        }
        if (this.script.parentNode) {
            this.renderer.removeChild(document.head, this.script);
        }
    }

    ngAfterViewInit(): void {
        this.translationService.getTranslation('de').subscribe(res => {
            this.germanTranslations = res;
        });

        // Turnstile must not be loaded twice, so a later visit renders with the already loaded api.js.
        if (this.isTurnstileAvailable()) {
            this.renderTurnstile();
            return;
        }
        (window as any)[TURNSTILE_ONLOAD_CALLBACK] = () => this.renderTurnstile();
        this.script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_ONLOAD_CALLBACK}`;
        this.renderer.appendChild(document.head, this.script);
    }

    onSubmitTicket(): void {
        this.setMessageValidity();
        this.validateInputs();
        const forms = document.getElementsByClassName('needs-validation');
        Array.prototype.filter.call(forms, form => form.classList.add('was-validated'));
        const token = this.captchaToken();
        if (!token) {
            return;
        }
        if (this.ticket.isFilled()) {
            // need ticketToSend and JSON.parse because without message and topic of ticket are overwritten and briefly shown in form
            this.parametersService.getParametersObservable().pipe(first(params => !!params.country.code)).subscribe({
                next: params => {
                    const locale = params.country.code.split('_');
                    const lang = locale[0];

                    const ticketToSend: Ticket = (structuredClone(this.ticket));
                    ticketToSend.message = this.getMessageWithHeader();
                    ticketToSend.topic = this.germanTranslations['topics'][this.ticket.topic];
                    ticketToSend.languageCode = lang;
                    ticketToSend.token = token;
                    // A Turnstile token is single-use; the server consumes it whether or not the request succeeds.
                    this.resetTurnstile();
                    this.contactService.sendContactForm(ticketToSend).subscribe({
                        next: () => {
                            this.notificationService.notifyWithTranslate('requestSent', 'issueWIP');
                            this.resetValidation();
                            this.ticket = new Ticket();
                        },
                        error: () => this.notificationService.notifyErrorWithTranslate('Error', 'tryAgainLater')
                    });
                },
                error: _ => this.notificationService.notifyErrorWithTranslate('Error', 'tryAgainLater')
            });
        }
    }

    private isTurnstileAvailable(): boolean {
        return typeof turnstile !== 'undefined';
    }

    private renderTurnstile(): void {
        const container = this.turnstileContainer()?.nativeElement;
        if (this.destroyed || !container || this.turnstileWidgetId !== null) {
            return;
        }
        this.turnstileWidgetId = turnstile.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            action: 'CONTACT',
            theme: 'light',
            // Stays hidden unless Cloudflare needs the visitor to interact with the challenge.
            appearance: 'interaction-only',
            'before-interactive-callback': () => this.captchaInteractive.set(true),
            'after-interactive-callback': () => this.captchaInteractive.set(false),
            callback: (token: string) => this.captchaToken.set(token),
            'expired-callback': () => this.captchaToken.set(null),
            'error-callback': () => this.captchaToken.set(null)
        });
    }

    private resetTurnstile(): void {
        this.captchaToken.set(null);
        if (this.turnstileWidgetId !== null && this.isTurnstileAvailable()) {
            turnstile.reset(this.turnstileWidgetId);
        }
    }

    setMessageValidity(): void {
        const textarea = document.getElementById('inputMessage') as HTMLTextAreaElement | null;
        if (textarea) {
            textarea.setCustomValidity(this.ticket.hasMultipleWords() ? '' : ' ');
        }
    }

    resetValidation(): void {
        const textarea = document.getElementById('inputMessage') as HTMLTextAreaElement | null;
        textarea?.setCustomValidity('');
        const forms = document.getElementsByClassName('needs-validation');
        Array.prototype.filter.call(forms, form => form.classList.remove('was-validated'), false);
    }

    validateInputs(): boolean {
        const forms = document.getElementsByClassName('needs-validation');
        let isValidated = true;
        Array.prototype.filter.call(forms, form => {
            form.addEventListener('submit', function (event) {
                if (form.checkValidity() === false) {
                    isValidated = false;
                    event.preventDefault();
                    event.stopPropagation();
                }
                form.classList.add('was-validated');
            }, false);
        });
        return isValidated;
    }

    getMessageWithHeader(): string {
        // check if user is logged in or not and if machine is selected
        let newMessage = `${this.ticket.firstName} ${this.ticket.lastName}\n`;

        if (this.user) {
            newMessage = `${newMessage + this.user.address.street}\n${this.user.address.postalCode} ${this.user.address.locality}\n${this.user.country}\n`;
        }
        newMessage = `${newMessage}${this.ticket.email}${(this.user ? '\n' : '\n\n')}`;

        if (this.user?.phoneNumber && this.user.phoneNumber.length > 3) {
            newMessage = `${newMessage}${this.user.phoneNumber}`;
        }

        let machine: Machine;
        if (this.user && this.userManager.userMachinesResource.value()) {
            machine = this.userManager.userMachinesResource.value().find(m => m.machineNumber === this.ticket.machineNumber);
        }

        newMessage = `${newMessage}${(machine ? ('\n\n' + machine.name + ' ') : '')}${this.germanTranslations['topics'][this.ticket.topic]}\n`;

        if (this.ticket.machineNumber) {
            newMessage = `${newMessage}Chassis Nummer   ${this.ticket.machineNumber} `;
        }

        if (machine) {
            newMessage = `${newMessage} \nSerialnummer     ${machine.sernr} `;
        }

        newMessage = `${newMessage} \n\nBenutzersprache: ${this.user?.language ?? '-'} \nMenüsprache: ${this.cookieService.get('locale')} \n\n${this.ticket.message} `;

        return newMessage;
    }
}
